import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Button,
  Alert,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import { getCustomHeatShrinkOptionsSync, getFinishingPricingSync } from '../services/pricingService';
import { sanitizeCustomHeatShrinkLine } from '../utils/customHeatShrinkHelpers';
import CustomHeatShrinkPreview from './CustomHeatShrinkPreview';

const MAX_LINE = 12;
const MAX_LOGO_DIMENSION_PX = 900; // Keep uploads small & fast; plenty for 1-color heat-shrink print
const TARGET_LOGO_MAX_BYTES = 350 * 1024; // Aim under typical server limits; adjust if needed

const defaultCustom = () => ({
  mode: 'text',
  attestedGrayscale: false,
  logo: { s3Key: '', publicUrl: '' },
  line1: '',
  line2: '',
  tubeColor: 'black',
  ribbonColor: 'white'
});

/** Text must contrast with tube: no black-on-black or white-on-white. */
function textColorsForTube(tubeColor, ribbonEnabled) {
  return ribbonEnabled.filter((c) => {
    if (tubeColor === 'black' && c === 'black') return false;
    if (tubeColor === 'white' && c === 'white') return false;
    return true;
  });
}

/**
 * Shared artwork for all ends marked "custom" heat shrink.
 */
function CustomHeatShrinkPanel({ value, onChange, sku, customEndCount }) {
  const opts = getCustomHeatShrinkOptionsSync();
  const tubeEnabled = useMemo(
    () => Object.entries(opts.tubeColors || {}).filter(([, on]) => on).map(([k]) => k),
    [opts]
  );
  const ribbonEnabled = useMemo(
    () => Object.entries(opts.ribbonColors || {}).filter(([, on]) => on).map(([k]) => k),
    [opts]
  );

  const ch = value && typeof value === 'object' ? { ...defaultCustom(), ...value } : defaultCustom();
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);

  const ribbonChoices = useMemo(
    () => textColorsForTube(ch.tubeColor, ribbonEnabled),
    [ch.tubeColor, ribbonEnabled]
  );

  useEffect(() => {
    if (ribbonChoices.length === 0) return;
    if (!ribbonChoices.includes(ch.ribbonColor)) {
      onChange({ ...ch, ribbonColor: ribbonChoices[0] });
    }
  }, [ch.tubeColor, ch.ribbonColor, ribbonChoices, onChange]);

  const patch = (partial) => {
    onChange({ ...ch, ...partial });
  };

  const formatBytes = (n) => {
    const bytes = Number(n) || 0;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round((bytes / 1024) * 10) / 10} KB`;
    return `${Math.round((bytes / (1024 * 1024)) * 100) / 100} MB`;
  };

  const downscaleLogoIfNeeded = async (file) => {
    try {
      const needsDownscale = file.size > TARGET_LOGO_MAX_BYTES;
      if (!needsDownscale) return file;

      // Decode image
      const url = URL.createObjectURL(file);
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });

      const srcW = img.naturalWidth || img.width;
      const srcH = img.naturalHeight || img.height;
      const maxSide = Math.max(srcW, srcH);

      const scale = maxSide > MAX_LOGO_DIMENSION_PX ? MAX_LOGO_DIMENSION_PX / maxSide : 1;
      const dstW = Math.max(1, Math.round(srcW * scale));
      const dstH = Math.max(1, Math.round(srcH * scale));

      const canvas = document.createElement('canvas');
      canvas.width = dstW;
      canvas.height = dstH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        return file;
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.clearRect(0, 0, dstW, dstH);
      ctx.drawImage(img, 0, 0, dstW, dstH);
      URL.revokeObjectURL(url);

      // Prefer PNG for logos; if still large, fall back to JPEG at quality steps.
      const toBlob = (type, quality) =>
        new Promise((resolve) => canvas.toBlob(resolve, type, quality));

      let blob = await toBlob('image/png');
      if (!blob) return file;

      if (blob.size > TARGET_LOGO_MAX_BYTES) {
        // Try JPEG with decreasing quality to hit the size target.
        for (const q of [0.9, 0.82, 0.74, 0.66]) {
          const candidate = await toBlob('image/jpeg', q);
          if (candidate && candidate.size < blob.size) blob = candidate;
          if (candidate && candidate.size <= TARGET_LOGO_MAX_BYTES) {
            blob = candidate;
            break;
          }
        }
      }

      const ext = blob.type === 'image/jpeg' ? 'jpg' : 'png';
      const safeBase = (file.name || 'logo').replace(/\.[^.]+$/, '');
      const outName = `${safeBase}-scaled.${ext}`;
      const outFile = new File([blob], outName, { type: blob.type });

      console.log('[HEATSHRINK][LOGO] Downscaled upload', {
        original: { name: file.name, type: file.type, size: formatBytes(file.size), w: srcW, h: srcH },
        output: { name: outFile.name, type: outFile.type, size: formatBytes(outFile.size), w: dstW, h: dstH }
      });

      return outFile;
    } catch (err) {
      console.warn('[HEATSHRINK][LOGO] Downscale failed, uploading original', err);
      return file;
    }
  };

  const safeParseJson = async (res) => {
    const text = await res.text();
    try {
      return { ok: true, data: JSON.parse(text) };
    } catch (e) {
      return { ok: false, text };
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !sku) {
      setUploadError(!sku ? 'Configuration SKU not ready — finish connector steps first.' : 'No file selected');
      return;
    }
    setUploadError('');
    setUploading(true);
    try {
      const uploadFile = await downscaleLogoIfNeeded(file);
      const fd = new FormData();
      fd.append('logo', uploadFile);
      fd.append('sku', sku);
      const res = await fetch('/api/upload-heat-shrink-logo', {
        method: 'POST',
        body: fd
      });
      const parsed = await safeParseJson(res);
      if (!parsed.ok) {
        // Common when server returns HTML error pages (e.g. 413).
        const hint = res.status === 413 ? 'File too large — try a smaller image.' : 'Server returned a non-JSON response.';
        console.error('[HEATSHRINK][LOGO] Upload failed (non-JSON response)', {
          status: res.status,
          statusText: res.statusText,
          bodyPrefix: String(parsed.text || '').slice(0, 200)
        });
        throw new Error(`${hint} (HTTP ${res.status})`);
      }

      const data = parsed.data;
      if (!res.ok || !data?.success) {
        const msg = data?.error || `Upload failed (HTTP ${res.status})`;
        console.error('[HEATSHRINK][LOGO] Upload failed (JSON)', { status: res.status, data });
        throw new Error(msg);
      }
      patch({
        mode: 'logo',
        logo: { s3Key: data.s3Key, publicUrl: data.publicUrl },
        line1: '',
        line2: ''
      });
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const unit = getFinishingPricingSync('heatShrink', 'custom');

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'action.hover'
      }}
    >
      <Typography variant="subtitle2" gutterBottom>
        2&quot; custom printed heat shrink
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        One artwork set for every end with custom printing: {customEndCount} end
        {customEndCount === 1 ? '' : 's'} × ${unit.toFixed(2)} = ${(unit * customEndCount).toFixed(2)} printing.
        Pick tube and text colors below; quantity discounts available—contact us.
      </Typography>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Preview (approximate)
      </Typography>
      <CustomHeatShrinkPreview value={ch} />

      <FormControl component="fieldset" sx={{ mb: 2, mt: 2 }}>
        <FormLabel component="legend">Artwork</FormLabel>
        <RadioGroup
          row
          value={ch.mode}
          onChange={(e) => {
            const mode = e.target.value;
            if (mode === 'logo') {
              patch({ mode: 'logo', line1: '', line2: '' });
            } else {
              patch({ mode: 'text', logo: { s3Key: '', publicUrl: '' }, attestedGrayscale: false });
            }
          }}
        >
          <FormControlLabel value="text" control={<Radio size="small" />} label="Text (up to 2 lines, 12 characters each)" />
          <FormControlLabel value="logo" control={<Radio size="small" />} label="Logo upload" />
        </RadioGroup>
      </FormControl>

      {tubeEnabled.length > 0 && ribbonEnabled.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Heat-shrink (tube) color</InputLabel>
            <Select
              label="Heat-shrink (tube) color"
              value={tubeEnabled.includes(ch.tubeColor) ? ch.tubeColor : tubeEnabled[0]}
              onChange={(e) => {
                const nextTube = e.target.value;
                const validText = textColorsForTube(nextTube, ribbonEnabled);
                const nextRibbon = validText.includes(ch.ribbonColor) ? ch.ribbonColor : validText[0];
                patch({ tubeColor: nextTube, ...(nextRibbon !== undefined ? { ribbonColor: nextRibbon } : {}) });
              }}
            >
              {tubeEnabled.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Text color</InputLabel>
            <Select
              label="Text color"
              value={
                ribbonChoices.includes(ch.ribbonColor)
                  ? ch.ribbonColor
                  : ribbonChoices[0] ?? ch.ribbonColor
              }
              onChange={(e) => patch({ ribbonColor: e.target.value })}
            >
              {ribbonChoices.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {ch.mode === 'text' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 420 }}>
          <TextField
            label="Line 1"
            size="small"
            value={ch.line1 || ''}
            inputProps={{ maxLength: MAX_LINE }}
            helperText={`${(ch.line1 || '').length}/${MAX_LINE}`}
            onChange={(e) => patch({ line1: sanitizeCustomHeatShrinkLine(e.target.value) })}
          />
          <TextField
            label="Line 2 (optional)"
            size="small"
            value={ch.line2 || ''}
            inputProps={{ maxLength: MAX_LINE }}
            helperText={`${(ch.line2 || '').length}/${MAX_LINE}`}
            onChange={(e) => patch({ line2: sanitizeCustomHeatShrinkLine(e.target.value) })}
          />
        </Box>
      )}

      {ch.mode === 'logo' && (
        <Box sx={{ mt: 1 }}>
          <Button variant="outlined" component="label" disabled={uploading || !sku}>
            {uploading ? 'Uploading…' : 'Choose logo file'}
            <input type="file" hidden accept="image/*" onChange={handleFile} />
          </Button>
          {ch.logo?.publicUrl && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Uploaded:{' '}
              <a href={ch.logo.publicUrl} target="_blank" rel="noopener noreferrer">
                view file
              </a>
            </Typography>
          )}
          <FormControlLabel
            sx={{ display: 'block', mt: 1 }}
            control={
              <Checkbox
                checked={ch.attestedGrayscale === true}
                onChange={(e) => patch({ attestedGrayscale: e.target.checked })}
              />
            }
            label="I confirm this logo is black and white / grayscale, suitable for single-color printing on the heat-shrink tube."
          />
        </Box>
      )}

      {uploadError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {uploadError}
        </Alert>
      )}
    </Box>
  );
}

export default CustomHeatShrinkPanel;
